import { useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { Btn } from "@/components/ui-kit";
import { REPORT_SECTIONS } from "@/components/VisitReport";
import { nextSessionNo, cycleText } from "@/components/Cycle";

export default function VisitReportForm({ c, prefill, onClose, onSubmit }) {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({
    visit_date: prefill?.date || today, visit_start: prefill?.start || "", visit_end: prefill?.end || "", location: "Patient's residence",
    height: "", weight: "", clinical_presentation: "", intervention: "",
    clinical_response: "", education: "", plan_of_care: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const oc = c.outcome || {};
  const sessionNo = nextSessionNo(oc);

  const submit = async () => {
    if (REPORT_SECTIONS.some(([k]) => !f[k].trim())) return toast.error("All 5 report sections are required");
    setBusy(true);
    try { await onSubmit(f); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save the report"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/50 backdrop-blur-sm p-4 overflow-y-auto" onMouseDown={onClose} data-testid="visit-report-form-overlay">
      <div onMouseDown={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8">
        <div className="flex items-start justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <div>
            <h3 className="font-display font-bold text-[17px] text-slate-900">Home visit report — session {sessionNo}{oc.sessions_total > 0 ? ` of ${oc.sessions_total}` : ""}{c.cycle > 1 ? ` · ${cycleText(c.cycle)}` : ""}</h3>
            <p className="text-[12.5px] text-slate-500 mt-0.5">{c.patient_name} · {c.diagnosis_label} · Saved to PranIQ Operations against the patient record</p>
          </div>
          <button onClick={onClose} data-testid="vrf-close" className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <div className="px-6 py-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
            <label className="block"><span className="pq-label">Visit date</span>
              <input type="date" className="pq-input py-1.5 w-full" value={f.visit_date} onChange={(e) => set("visit_date", e.target.value)} data-testid="vrf-date" /></label>
            <label className="block"><span className="pq-label">Start time</span>
              <input type="time" className="pq-input py-1.5 w-full" value={f.visit_start} onChange={(e) => set("visit_start", e.target.value)} data-testid="vrf-start" /></label>
            <label className="block"><span className="pq-label">End time</span>
              <input type="time" className="pq-input py-1.5 w-full" value={f.visit_end} onChange={(e) => set("visit_end", e.target.value)} data-testid="vrf-end" /></label>
            <label className="block"><span className="pq-label">Location</span>
              <input className="pq-input py-1.5 w-full" value={f.location} onChange={(e) => set("location", e.target.value)} data-testid="vrf-location" /></label>
            <label className="block"><span className="pq-label">Height (cm)</span>
              <input className="pq-input py-1.5 w-full" value={f.height} onChange={(e) => set("height", e.target.value)} placeholder="optional" data-testid="vrf-height" /></label>
            <label className="block"><span className="pq-label">Weight (kg)</span>
              <input className="pq-input py-1.5 w-full" value={f.weight} onChange={(e) => set("weight", e.target.value)} placeholder="optional" data-testid="vrf-weight" /></label>
          </div>
          {REPORT_SECTIONS.map(([k, label], i) => (
            <label key={k} className="block mb-3">
              <span className="pq-label">{i + 1}. {label} <span className="text-rose-500">*</span></span>
              <textarea rows={3} className="pq-input py-1.5 w-full" value={f[k]} onChange={(e) => set(k, e.target.value)}
                placeholder="One point per line — each line becomes a bullet on the report" data-testid={`vrf-${k}`} />
            </label>
          ))}
          <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100">
            <Btn onClick={onClose} data-testid="vrf-cancel">Cancel</Btn>
            <Btn variant="primary" onClick={submit} disabled={busy} data-testid="vrf-submit">
              {busy ? "Saving…" : "Submit report & log session"}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}
